
import { Component, Vue, Emit, Watch } from 'vue-property-decorator';
import { FeatureCollection } from '../../../../shared/types/MapShapes';
import { validationMessages } from '../../validationsMessages';
import { normalizePolygonCoords, convertDecimalListToSexagesimal } from '../../../../core/utils/map-utils';
import { convertValueToNumber, EventBus, TypeEventBus } from '../../../../core/utils';
import { latLngAreValid } from '../../../item/validations';

interface Coordinate {
  ringIndex: number;
  lat: number | null;
  lng: number | null;
  isEditing: boolean;
  originalLat: number | null;
  originalLng: number | null;
}

@Component({})
export default class DecimalFormatTable extends Vue {
  coordinatesList: Coordinate[] = [];
;

  headers = [
    { text: 'Latitud', value: 'lat' },
    { text: 'Longitud', value: 'lng' },
    { text: 'Acciones', value: 'actions', sortable: false, width: '100px' }
  ];

  get isEditingRow(): boolean {
    return this.coordinatesList.some(item => item.isEditing);
  }

  get notEnoughPoints(): boolean {
    return this.coordinatesList.length < 3;
  }
  
  canAddPoint(item):boolean{
    return this.latLngAreCorrect(item.lat, item.lng)
  }
  
  get saveStatusMessage(): string {
    if (this.notEnoughPoints){
      return validationMessages.NOT_ENOUGH_POINTS
    };
    if (this.isEditingRow){
      return validationMessages.POINT_WITHOUT_SAVE;
    }

    return '';
  }

  latLngAreCorrect(lat: string | number | null, lng: string | number | null): boolean {
    return latLngAreValid(lat, lng);
  }

  addRow() {
    this.coordinatesList.unshift({
      lat: null,
      lng: null,
      ringIndex: 0,
      isEditing: true,
      originalLat: null,
      originalLng: null
    });
  }

  clickSavePoint(row: Coordinate) {
      if (!this.latLngAreCorrect(row.lat, row.lng)){
        this.saveStatusChanged(validationMessages.NOT_VALID_LAT_LONG);
        EventBus.$emit(TypeEventBus.SHOW_MESSAGE, validationMessages.NOT_VALID_LAT_LONG, 'error');
        return
      }

      row.originalLat = row.lat;
      row.originalLng = row.lng;
      this.$set(row, 'isEditing', false);
  }

  cancelEdit(row: Coordinate) {
    if (row.originalLat === null || row.originalLng === null) {
      this.deleteRow(row);
      return;
    }
    row.lat = row.originalLat;
    row.lng = row.originalLng;
    this.$set(row, 'isEditing', false);
  }

  editRow(row: Coordinate) {
    row.originalLat = row.lat;
    row.originalLng = row.lng;
    this.$set(row, 'isEditing', true);
  }

  deleteRow(row: Coordinate) {
    const index = this.coordinatesList.indexOf(row);
    if (index !== -1) this.coordinatesList.splice(index, 1);
  }

  updateCoordinatesList(coordsByRing: google.maps.LatLngLiteral[][]) {
    this.coordinatesList = coordsByRing.flatMap((ring, ringIndex) =>
      ring.map(coord => ({
        ringIndex,
        lat: coord.lat,
        lng: coord.lng,
        isEditing: false,
        originalLat: coord.lat,
        originalLng: coord.lng
      }))
    );
  }


  

  getGeoJsonData(): FeatureCollection {
    const ringsMap = new Map<number, Coordinate[]>();
    this.coordinatesList
      .filter(c => this.latLngAreCorrect(c.lat, c.lng))
      .forEach(c => {
        if (!ringsMap.has(c.ringIndex)) ringsMap.set(c.ringIndex, []);
        ringsMap.get(c.ringIndex)!.push(c);
      });


    const rings = Array.from(ringsMap.values()).map(coords => {
      const normalized = normalizePolygonCoords(
        coords.map(c => {
          const coord = { 
            lat: convertValueToNumber(c.lat) as number, 
            lng: convertValueToNumber(c.lng) as number 
          };
          return coord;
        })
      );
      return normalized.map(c => [c.lng, c.lat]);
    });

    return {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: {
            type: "MultiPolygon",
            coordinates: [rings]
          },
          properties: {}
        }
      ]
    };
  }



  clickPreviewPolygonsInMap() {
    const geoJson = this.getGeoJsonData();
    this.geojsonToPreview(geoJson);
  }

  clickSavePolygon() {
    const geoJson = this.getGeoJsonData();
    if (geoJson) this.updatePolygon(geoJson);
  }

  getCoordinatesInSexagesimal() {
    const ringsMap = new Map<number, Coordinate[]>();

    this.coordinatesList.forEach(c => {
      if (!ringsMap.has(c.ringIndex)) ringsMap.set(c.ringIndex, []);
      ringsMap.get(c.ringIndex)!.push(c);
    });

    const grouped = Array.from(ringsMap.values()).map(coords =>
      coords.map(c => ({ lat: c.lat!, lng: c.lng! }))
    );

    return convertDecimalListToSexagesimal(grouped);
  }

  cancelChanges(): void {
    this.coordinatesList.forEach((row) => {
      if (row.isEditing) this.cancelEdit(row);
    });
  }

  @Watch('saveStatusMessage', { immediate: true })
  onSaveStatusMessageChanged(newMessage: string) {
    this.saveStatusChanged(newMessage);
  }

  @Emit()
  saveStatusChanged(message: string) {}

  @Emit()
  geojsonToPreview(geoJson: FeatureCollection) {}

  @Emit()
  updatePolygon(geoJson: FeatureCollection) {}
}
